<template>
  <v-row justify="center">
    <v-dialog
      v-model="dialog"
      persistent
      scrollable
      max-width="70%">
      <v-card
        v-if="mode === 'select'">
        <v-card-title class="headline font-weight-bold">
          <h3>Fabrics</h3>
          <v-spacer />
          <v-btn
            icon
            @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text style="height: 700px;">
          <v-container fluid>
            <v-row
              justify="space-between"
              align="center">
              <v-col cols="12">
                <v-spacer />
                <div class="d-flex flex-row justify-end align-center">
                  <v-btn
                    class="mr-2"
                    @click="swapToCreateForm">
                    <v-icon>mdi-plus</v-icon>
                    CREATE NEW
                  </v-btn>
                  <search-box
                    v-model="filters.search"
                    :disabled="loading"
                    @on-search="getFabrics(true)" />
                </div>
              </v-col>
            </v-row>

            <v-data-table
              class="mt-5"
              :headers="fabricHeaders"
              :items="fabrics"
              :items-per-page="filters.limit"
              :loading="loading"
              hide-default-footer>
              <template #[`item.image`]="{ item }">
                <gw-product-image
                  v-if="item.imageUrls.length"
                  :src="item.imageUrls[0]"
                  class="cursor-pointer py-3"
                  @click="previewImg(item.imageUrls[0])" />
              </template>
              <template #[`item.size`]="{ item }">
                {{ item.size || '-' }}
              </template>
              <template #[`item.color`]="{ item }">
                {{ item.color || '-' }}
              </template>
              <template #[`item.unitPrice`]="{ item }">
                {{ item.unitPrice | showFullPriceFormat() }} {{ item.currency.toUpperCase() }}
              </template>
              <template #[`item.actions`]="{ item }">
                <v-btn
                  color="secondary"
                  outlined
                  :loading="loading"
                  @click="onSelect(item)">
                  Select
                </v-btn>
              </template>
            </v-data-table>

            <v-pagination
              v-model="filters.page"
              :total-visible="7"
              :length="pageCount"
              :disabled="loading" />

            <img-popup-preview
              v-model="previewEnabled"
              :img-src="currentImgPreview" />
          </v-container>
        </v-card-text>
      </v-card>
      <v-card v-else-if="mode === 'form'">
        <v-card-title class="headline font-weight-bold">
          <h3 v-if="updateFabricId">
            Edit Fabric
          </h3>
          <h3 v-else>
            <v-btn
              icon
              @click="mode = 'select'">
              <v-icon>mdi-arrow-left</v-icon>
            </v-btn> Create Fabric
          </h3>
          <v-spacer />
          <v-btn
            icon
            @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text style="height: 700px;">
          <FabricForm
            :is-component="true"
            :fabric="updateFabricId"
            @onSave="onSave($event)" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { mapActions } from 'vuex'
import FabricsProvider from '@/resources/FabricsProvider'
import SearchBox from '@/components/SearchBox.vue'
import ImgPopupPreview from '@/components/ImgModalPreview.vue'
import FabricForm from '../page/FabricForm.vue'

const FabricsService = new FabricsProvider()

export default {
  components: {
    SearchBox,
    ImgPopupPreview,
    FabricForm
  },
  props: {
    value: {
      type: Boolean,
      default: false
    },
    updateFabricId: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      mode: 'select', // form
      expanded: [],
      dialogFabricForm: false,
      loading: false,
      previewEnabled: false,
      currentImgPreview: null,
      fabrics: [],
      pageCount: 0,
      filters: {
        search: '',
        page: 1,
        limit: 10
      },
      fabricHeaders: [
        {
          text: '',
          value: 'image',
          align: 'center',
          sortable: false
        },
        {
          text: 'รหัสแฮงค์',
          value: 'hangId',
          align: 'center',
          sortable: false
        },
        {
          text: 'รหัสผ้า',
          value: 'fabricNo',
          align: 'center',
          sortable: false
        },
        {
          text: 'สถานที่สั่ง',
          value: 'factory',
          align: 'center',
          sortable: false
        },
        {
          text: 'ขนาด',
          value: 'size',
          align: 'center',
          sortable: false
        },
        {
          text: 'สี',
          value: 'color',
          align: 'end',
          sortable: false
        },
        {
          text: 'ราคาต่อหน่วย',
          value: 'unitPrice',
          align: 'center',
          sortable: false
        },
        {
          text: 'คอลเลคชั่น',
          value: 'collectionName',
          align: 'center',
          sortable: false
        },
        {
          text: '',
          value: 'actions',
          align: 'center',
          sortable: false
        }
      ]
    }
  },
  computed: {
    dialog: {
      get () {
        return this.value
      },
      set (val) {
        this.$emit('input', val)
        if (!val) this.$emit('on-close')
      }
    }
  },
  watch: {
    'filters.page': {
      handler () {
        this.getFabrics()
      },
      deep: true
    }
  },
  mounted () {
    if (this.updateFabricId) {
      this.mode = 'form'
    } else {
      this.getFabrics(true)
    }
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar',
      setLoading: 'Components/setLoading'
    }),
    async getFabrics (isClear = false) {
      try {
        this.loading = true

        if (isClear) {
          this.fabrics = []
          this.filters.page = 1
        }

        const { data } = await FabricsService.getAll({
          search: this.filters.search,
          page: this.filters.page,
          limit: this.filters.limit
        })

        this.fabrics = data.results
        this.pageCount = data.pages
      } catch (error) {
        console.error('getFabrics', error)
        this.setSnackbar({
          value: true,
          message: `Error: ${error.message}`,
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    previewImg (img) {
      this.currentImgPreview = img
      this.previewEnabled = !!this.currentImgPreview
    },
    onSelect (item) {
      const items = {
        ...item,
        id: item.id,
        patterns: [
          {
            note: '',
            imageUrls: []
          }
        ]
      }

      this.$emit('on-select', items)
    },
    closeForm () {
      this.mode = 'select'
      this.$emit('on-close')
    },
    gotoCreateFabric () {
      this.dialog = false
      const routeData = this.$router.resolve({ name: 'NewFabricForm' })
      window.open(routeData.href, '_blank')
    },
    onSave (v) {
      this.$emit('on-select', v)
    },
    swapToCreateForm () {
      this.mode = 'form'
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
.uppercase {
  text-transform: uppercase;
}
</style>
